/**
 * A2A Staff Service
 * Wraps staff operations using A2A client instead of direct Supabase calls
 * 
 * NOTE: Backend A2A capabilities for Staff are not yet implemented.
 * This service is prepared for when backend capabilities are added.
 * For now, it will fall back to direct Supabase calls.
 */

import { A2AClient } from '../client.js';
import { supabaseAdmin } from '../../lib/supabase.js';

// Initialize A2A client
const a2aClient = new A2AClient(
  process.env.REACT_APP_A2A_BACKEND_URL || process.env.REACT_APP_API_URL,
  'myclass-client-staff',
  {
    secretKey: process.env.REACT_APP_A2A_SECRET_KEY,
    enableLogging: process.env.NODE_ENV === 'development'
  }
);

// Map tenant_id strings to actual UUID tenant IDs
const tenantIdMap = {
  'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
  'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
  'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
};

/**
 * A2A Staff Service
 * Currently uses direct Supabase until backend A2A capabilities are implemented
 */
export const a2aStaffService = {
  /**
   * Get staff data
   * TODO: Migrate to A2A capability once backend implements list_staff
   */
  async getStaffData(tenantId = 'tn_01') {
    try {
      const actualTenantId = tenantIdMap[tenantId] || tenantId;
      
      console.log(`[A2A Staff] Fetching staff data for tenant ${tenantId} -> ${actualTenantId}`);

      // TODO: Once backend implements list_staff capability, use this:
      // const result = await a2aClient.executeTask('list_staff', {
      //   tenant_id: actualTenantId
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, use direct Supabase (with shared client)
      const { data: staffProfiles, error: staffError } = await supabaseAdmin
        .from('staff_profiles')
        .select('*')
        .eq('tenant_id', actualTenantId)
        .order('department', { ascending: true });
      
      if (staffError) {
        console.error('[A2A Staff] Error fetching staff profiles:', staffError);
        return { success: false, data: null, error: staffError.message };
      }

      console.log(`[A2A Staff] Staff profiles fetched: ${staffProfiles?.length || 0}`);

      // Process staff data
      const totalStaff = staffProfiles?.length || 0;
      
      // Group staff by department
      const departmentGroups = {};
      staffProfiles?.forEach(staff => {
        const dept = staff.department || 'Other';
        if (!departmentGroups[dept]) {
          departmentGroups[dept] = [];
        }
        departmentGroups[dept].push(staff);
      });

      const totalDepartments = Object.keys(departmentGroups).length;
      const totalPositions = totalStaff;

      // Create department statistics with colors
      const departmentStats = {};
      const colors = ['#3182ce', '#38a169', '#d69e2e', '#e53e3e', '#805ad5', '#dd6b20', '#2d3748', '#c53030', '#319795', '#d53f8c'];
      let colorIndex = 0;
      
      Object.entries(departmentGroups).forEach(([deptName, staff]) => {
        departmentStats[deptName] = {
          name: deptName,
          count: staff.length,
          staff: staff,
          color: colors[colorIndex % colors.length]
        };
        colorIndex++;
      });

      // Get recent staff (last 5 registered)
      const recentStaff = staffProfiles
        ?.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 5) || [];

      // Group staff by category
      const teachingAssistants = staffProfiles?.filter(s => 
        s.position?.includes('Teaching Assistant') || s.position?.includes('SEN') || s.position?.includes('助教')
      ) || [];
      const labTechnicians = staffProfiles?.filter(s => 
        s.position?.includes('Lab Technician') || s.position?.includes('實驗室技術員')
      ) || [];
      const computerTechnicians = staffProfiles?.filter(s => 
        s.position?.includes('Computer Technician') || s.position?.includes('電腦技術員')
      ) || [];
      const supportStaff = staffProfiles?.filter(s => 
        s.position?.includes('Social Worker') || s.position?.includes('Counsellor') || 
        s.position?.includes('Psychologist') || s.position?.includes('Therapist') ||
        s.position?.includes('輔導員') || s.position?.includes('心理輔導師')
      ) || [];
      const administrativeStaff = staffProfiles?.filter(s => 
        s.position?.includes('Clerk') || s.position?.includes('Accountant') || 
        s.position?.includes('Executive Officer') || s.position?.includes('行政') ||
        s.position?.includes('會計')
      ) || [];
      const schoolLabourers = staffProfiles?.filter(s => 
        s.position?.includes('School Labourer') || s.position?.includes('工友')
      ) || [];
      const securityStaff = staffProfiles?.filter(s => 
        s.position?.includes('Security') || s.position?.includes('保安')
      ) || [];

      const staffByCategory = {
        teachingAssistants,
        labTechnicians,
        computerTechnicians,
        supportStaff,
        administrativeStaff,
        schoolLabourers,
        securityStaff
      };

      const summaryData = {
        totalStaff,
        totalDepartments,
        totalPositions,
        departmentStats,
        recentStaff,
        staffByCategory,
        staff: staffProfiles || [],
        mockData: false
      };

      return { success: true, data: summaryData, error: null };

    } catch (err) {
      console.error('[A2A Staff] Unexpected error:', err);
      return { success: false, data: null, error: err.message || 'Unexpected error' };
    }
  },

  /**
   * Get staff by ID
   * TODO: Migrate to A2A capability once backend implements get_staff
   */
  async getStaffById(staffId, tenantId = 'tn_01') {
    try {
      const actualTenantId = tenantIdMap[tenantId] || tenantId;
      
      // TODO: Once backend implements get_staff capability, use this:
      // const result = await a2aClient.executeTask('get_staff', {
      //   tenant_id: actualTenantId,
      //   staff_id: staffId
      // });
      // return {
      //   success: result.success,
      //   data: result.data || null,
      //   error: result.error || null
      // };

      // For now, use direct Supabase
      const { data, error } = await supabaseAdmin
        .from('staff_profiles')
        .select('*')
        .eq('id', staffId)
        .eq('tenant_id', actualTenantId)
        .single();

      if (error) {
        return { success: false, data: null, error: error.message };
      }

      return { success: true, data, error: null };
    } catch (err) {
      return { success: false, data: null, error: err.message };
    }
  }
};

